import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckCircle2, X } from 'lucide-react';
const NAMES = ['María', 'Carlos', 'Jennifer', 'Miguel', 'Ana', 'David', 'Roberto', 'Jessica', 'Luis', 'Carmen', 'John', 'Pedro', 'Sofia', 'Michael', 'Isabella', 'José', 'Laura', 'Daniel', 'Alejandra', 'James', 'Rosa', 'Fernando', 'Patricia', 'Diego', 'Valentina'];
const CITIES = ['Miami', 'Los Angeles', 'Houston', 'New York', 'Chicago', 'San Antonio', 'Dallas', 'Phoenix', 'San Diego', 'Denver', 'Orlando', 'Las Vegas', 'Austin', 'Atlanta', 'Tampa', 'Sacramento', 'El Paso', 'San Jose', 'Charlotte', 'Tucson'];
interface NotificationData {
  id: string;
  name: string;
  city: string;
  timeAgo: number;
}
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [usedNames, setUsedNames] = useState<string[]>([]);
  const prefersReducedMotion = useReducedMotion();
  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const generateNotification = useCallback(() => {
    // Filter out recently used names
    let availableNames = NAMES.filter(name => !usedNames.includes(name));
    // If we've used all names (or almost all), reset the history
    if (availableNames.length === 0) {
      availableNames = NAMES;
      setUsedNames([]);
    }
    const name = getRandomItem(availableNames);
    const city = getRandomItem(CITIES);
    const timeAgo = Math.floor(Math.random() * 15) + 1; // 1 to 15 minutes
    // Update used names history (keep last 10)
    setUsedNames(prev => {
      const newHistory = [...prev, name];
      if (newHistory.length > 10) {
        return newHistory.slice(newHistory.length - 10);
      }
      return newHistory;
    });
    setCurrentNotification({
      id: Math.random().toString(36).substring(7),
      name,
      city,
      timeAgo
    });
    setIsVisible(true);
    // Auto dismiss after 4.5 seconds
    setTimeout(() => {
      setIsVisible(false);
    }, 4500);
  }, [usedNames]);
  useEffect(() => {
    // Initial delay of 3 seconds
    const initialTimer = setTimeout(() => {
      generateNotification();
    }, 3000);
    return () => clearTimeout(initialTimer);
  }, []); // Empty dependency array so this only runs once on mount
  useEffect(() => {
    if (!isVisible && currentNotification) {
      // Schedule next notification when current one hides
      // Random interval between 8 and 15 seconds
      const nextDelay = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
      const timer = setTimeout(() => {
        generateNotification();
      }, nextDelay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, currentNotification, generateNotification]);
  const handleDismiss = () => {
    setIsVisible(false);
  };
  const animationVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 80,
      scale: prefersReducedMotion ? 1 : 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 80,
      scale: prefersReducedMotion ? 1 : 0.95,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence data-id="element-275">
      {isVisible && currentNotification && <motion.div key={currentNotification.id} variants={animationVariants} initial="hidden" animate="visible" exit="exit" className="fixed bottom-6 left-0 right-0 mx-auto z-50 w-[90%] max-w-[400px]" data-id="element-276">
          <div className="relative bg-navy-midnight border-l-4 border-gold rounded-lg shadow-[0_4px_20px_rgba(201,168,76,0.15)] p-4 pr-10 overflow-hidden" data-id="element-277">
            {/* Subtle background glow */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-gold/5 to-transparent pointer-events-none" data-id="element-278"></div>

            <button onClick={handleDismiss} className="absolute top-3 right-3 text-body/50 hover:text-body transition-colors" aria-label="Cerrar notificación" data-id="element-279">
              <X className="w-4 h-4" data-id="element-280" />
            </button>

            <div className="flex items-start gap-3 relative z-10" data-id="element-281">
              {/* Avatar */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navy-dark border border-gold/30 flex items-center justify-center text-gold font-headline font-bold text-lg shadow-inner" data-id="element-282">
                {currentNotification.name.charAt(0)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-283">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-284">
                  <p className="text-sm text-body font-body truncate" data-id="element-285">
                    <span className="font-bold text-white" data-id="element-286">
                      {currentNotification.name}
                    </span>{' '}
                    en {currentNotification.city}
                  </p>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" data-id="element-287" />
                </div>

                <p className="text-sm font-headline font-bold text-gold mb-1 truncate" data-id="element-288">
                  Rompe El Código Del Dinero
                </p>

                <div className="flex items-center justify-between" data-id="element-289">
                  <p className="text-xs text-body/70 font-body" data-id="element-290">
                    Hace {currentNotification.timeAgo}{' '}
                    {currentNotification.timeAgo === 1 ? 'minuto' : 'minutos'}
                  </p>
                  <p className="text-xs font-bold text-white bg-navy-dark px-2 py-0.5 rounded border border-gold/20" data-id="element-291">
                    $6.36 USD
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}